<template>
  <div class="svcx" :style="themeVars">
    <!-- 1 · CÓMO FUNCIONA + LAS PREGUNTAS + PRUEBA SOCIAL -->
    <template v-if="block === 'info'">
      <section v-if="pasos.length" class="svcx-sec">
        <div class="svcx-head">
          <span class="svcx-eyebrow">{{ content.teaser_eyebrow || "Cómo funciona" }}</span>
          <h2>
            {{ content.teaser_title || "Así funciona" }}
            <template v-if="content.teaser_title_slam"><br /><span class="svcx-slam">{{ content.teaser_title_slam }}</span></template>
          </h2>
        </div>
        <ol class="svcx-pasos">
          <li v-for="(paso, i) in pasos" :key="`paso-${i}`" :style="{ '--d': i * 0.12 + 's' }">
            <span class="svcx-n">{{ i + 1 }}</span>
            <div>
              <b>{{ paso[0] }}</b>
              <p>{{ paso[1] }}</p>
            </div>
          </li>
        </ol>
      </section>

      <!-- El porqué: lo que pasa en la sala, la cadena que se rompe y lo que
           responde el DUA. Va primero porque es lo que da sentido al resto. -->
      <section v-if="porque.lede" class="svcx-sec">
        <div class="svcx-head">
          <span class="svcx-eyebrow">El porqué</span>
          <h2>¿Por qué diversificar<br />
            <span class="svcx-slam svcx-slam--rojo">con el DUA?</span></h2>
          <!-- `lede`, `nota` y `respuesta` traen <b>: es HTML escrito en
               pages.py, igual que la cita del Mineduc. -->
          <p class="svcx-lede" v-html="porque.lede"></p>
        </div>

        <h3 class="svcx-h3">{{ porque.cadena_titulo }}</h3>
        <p class="svcx-p">{{ porque.cadena_lede }}</p>
        <ol class="svcx-cadena">
          <li
            v-for="(eslabon, i) in porque.cadena || []"
            :key="eslabon.etapa"
            :style="{ '--c': eslabon.color, '--d': i * 0.12 + 's' }"
          >
            <img v-if="eslabon.img" :src="eslabon.img" alt="" loading="lazy" />
            <b>{{ eslabon.etapa }}</b>
            <span>{{ eslabon.que }}</span>
          </li>
        </ol>
        <p v-if="porque.nota" class="svcx-nota" v-html="porque.nota"></p>

        <div class="svcx-respuesta">
          <span class="svcx-eyebrow">{{ porque.respuesta_eyebrow }}</span>
          <h3>{{ porque.respuesta_titulo }}</h3>
          <p v-html="porque.respuesta"></p>
        </div>
      </section>

      <!-- Los tres principios del DUA. Van como paneles del color de cada uno,
           igual que en la página: es su propio bloque, no una lista más. -->
      <section v-if="principios.length" class="svcx-sec">
        <div class="svcx-head">
          <span class="svcx-eyebrow">Tres principios, seis estrategias</span>
          <h2>Lo que hace la planificación<br />
            <span class="svcx-slam svcx-slam--rosa">en cada clase</span></h2>
        </div>
        <ul class="svcx-principios">
          <li
            v-for="p in principios"
            :key="p.n"
            :style="{ '--c': p.color, '--pink': p.ink || '#fff', '--d': p.delay }"
          >
            <b>{{ p.n }} · {{ p.nombre }}</b>
            <span>{{ p.lema }}</span>
            <em>{{ p.estrategias }}</em>
          </li>
        </ul>
      </section>

      <section v-if="entrega.length" class="svcx-sec">
        <div class="svcx-head">
          <span class="svcx-eyebrow">Lo que te llevas</span>
          <h2>Los dos archivos,<br />
            <span class="svcx-slam svcx-slam--azul">listos para editar</span></h2>
        </div>
        <ul class="svcx-incluye">
          <li
            v-for="item in entrega"
            :key="item.nombre"
            :style="{ '--c': item.color, '--d': item.delay }"
          >
            <b>{{ item.nombre }}</b>
            <p>{{ item.detalle }}</p>
          </li>
        </ul>
      </section>

      <section v-if="incluye.length" class="svcx-sec">
        <div class="svcx-head">
          <span class="svcx-eyebrow">Qué incluye</span>
          <h2>Lo que puedes<br /><span class="svcx-slam">trabajar con esto</span></h2>
        </div>
        <ul class="svcx-incluye">
          <li v-for="(item, i) in incluye" :key="`inc-${i}`" :style="{ '--d': i * 0.1 + 's' }">
            <b>{{ item.nombre }}</b>
            <p>{{ item.detalle }}</p>
          </li>
        </ul>
      </section>

      <!-- Las objeciones con las que llega quien está pensando en cambiar de
           prueba. Cada una trae su color en `p.color` como `var(--tertiary)` y
           compañía: los nombres son los de site.css y están redeclarados abajo,
           así que el valor de la página resuelve tal cual acá. -->
      <section v-if="preguntas.length" class="svcx-sec">
        <div class="svcx-head">
          <span class="svcx-eyebrow">Decreto 170</span>
          <h2>Las preguntas que todas se hacen<br />
            <span class="svcx-slam svcx-slam--oscuro">antes de decidir</span></h2>
        </div>
        <ul class="svcx-preguntas">
          <li
            v-for="(p, i) in preguntas"
            :key="`q-${i}`"
            :style="{ '--c': p.color, '--d': i * 0.12 + 's' }"
          >
            <span>
              <b>{{ p.q }}</b>
              <span class="svcx-r"><strong>{{ p.fuerte }}</strong> {{ p.r }}</span>
            </span>
          </li>
        </ul>
      </section>

      <!-- La respuesta oficial, que es a donde apunta la segunda pregunta.
           El vídeo va en youtube-nocookie y diferido, igual que en la página:
           no deja cookies hasta que alguien le da al play. -->
      <section v-if="mineduc.video" class="svcx-sec">
        <div class="svcx-head">
          <span class="svcx-eyebrow">Respuesta oficial</span>
          <h2>¿Qué dice<br /><span class="svcx-slam svcx-slam--rojo">el Mineduc?</span></h2>
        </div>
        <div class="svcx-mineduc">
          <div class="svcx-marco">
            <span class="svcx-rotulo">{{ mineduc.rotulo }}</span>
            <iframe
              :src="mineduc.video"
              :title="mineduc.titulo"
              loading="lazy"
              allowfullscreen
              referrerpolicy="no-referrer-when-downgrade"
            ></iframe>
          </div>
          <div class="svcx-cita">
            <!-- `cita` trae los <mark> que resaltan las dos frases. Es HTML
                 nuestro, escrito en pages.py, no texto de un tercero. -->
            <blockquote v-html="mineduc.cita"></blockquote>
            <cite>{{ mineduc.fuente }}</cite>
          </div>
        </div>
      </section>

      <section v-if="cifras.length || usan.length" class="svcx-sec">
        <div class="svcx-head">
          <span class="svcx-eyebrow">Ya la usan</span>
          <h2>{{ cifraTitular }}<br /><span class="svcx-slam svcx-slam--teal">hoy en Chile</span></h2>
        </div>
        <ul v-if="cifras.length" class="svcx-cifras">
          <li v-for="(c, i) in cifras" :key="`c-${i}`" :style="{ '--d': i * 0.1 + 's' }">
            <b>
              <small v-if="c.antes">{{ c.antes }}</small>{{ c.dato
              }}<small v-if="c.sufijo"> {{ c.sufijo }}</small>
            </b>
            <span>{{ c.que }}</span>
          </li>
        </ul>
        <ul v-if="usan.length" class="svcx-usan">
          <li v-for="(u, i) in usan" :key="`u-${i}`">{{ u }}</li>
        </ul>
      </section>
    </template>

    <!-- 2 · PRECIOS -->
    <section v-else-if="block === 'planes'" class="svcx-sec">
      <div class="svcx-head">
        <span class="svcx-eyebrow">Cuánto cuesta</span>
        <h2>Empieza gratis.<br /><span class="svcx-slam svcx-slam--teal">Paga solo lo que uses</span></h2>
      </div>
      <ul class="svcx-planes">
        <li
          v-for="(plan, i) in planes"
          :key="`plan-${i}`"
          :class="{ 'svcx-plan--destacado': plan.destacado }"
          :style="{ '--d': i * 0.12 + 's' }"
        >
          <div class="svcx-precio">
            {{ plan.precio }}<small>{{ plan.unidad }}</small>
          </div>
          <div>
            <b>{{ plan.titulo }}</b>
            <p>{{ plan.detalle }}</p>
          </div>
          <button
            v-if="abreLogin(plan)"
            type="button"
            class="svcx-btn"
            @click="$emit('go-to-step', 'login')"
          >
            {{ plan.cta }}
          </button>
          <a v-else class="svcx-btn" :href="plan.href">{{ plan.cta }}</a>
        </li>
      </ul>
    </section>

    <!-- 3 · PREGUNTAS FRECUENTES -->
    <section v-else-if="block === 'faq'" class="svcx-sec">
      <div class="svcx-head">
        <span class="svcx-eyebrow">Preguntas</span>
        <h2>Lo que nos preguntan<br /><span class="svcx-slam svcx-slam--oscuro">antes de usarla</span></h2>
      </div>
      <div class="svcx-faq">
        <details v-for="(par, i) in faq" :key="`faq-${i}`">
          <summary>{{ par[0] }}</summary>
          <div>{{ par[1] }}</div>
        </details>
      </div>
    </section>
  </div>
</template>

<script>
// Las secciones de una página /<servicio>/ dentro del chat.
//
// Existe porque el contenido de esas páginas metido en las tarjetas de una
// burbuja de chat se ve diminuto: la burbuja es angosta y todo —cifras,
// planes, preguntas frecuentes— quedaba en el mismo cuerpo de 0,9rem. Un paso
// `custom-component` se dibuja FUERA de la burbuja (ver `.box-answer` en
// ChatMessageComponent), así que acá hay ancho completo y se puede repetir el
// diseño real de la página: antetítulo + titular con su franja de color,
// tarjetas de cifra, planes en fila y el FAQ como acordeón.
//
// Los estilos son un port de las mismas reglas de
// backend/marketing/static/marketing/css/site.css. Es una copia y se sabe: el
// SPA no puede cargar esa hoja (vive en otro documento y usa `{% static %}`),
// pero es una copia de FORMA, no de contenido — los textos y los precios
// siguen bajando del endpoint, que es lo que no se podía duplicar.
export default {
  name: "ServiceContent",
  props: {
    // El JSON de `informationService.getServiceContent`.
    content: { type: Object, required: true },
    // Qué sección dibujar: "info", "planes" o "faq".
    block: { type: String, required: true },
    // El acento del producto (#f8ac04 en Eureka). `ink` es su versión oscura
    // para texto: el ámbar sobre blanco da 1,93:1 y no se puede leer.
    color: { type: String, default: "#f8b500" },
    colorInk: { type: String, default: "#c28e02" },
  },
  methods: {
    // Ningún CTA que entre a la aplicación le sirve a quien está viendo la
    // demo sin sesión, así que abre el modal de acceso en vez de mandarlo a
    // una pantalla que se la va a pedir igual. Son dos: el plan gratis, cuyo
    // href recargaría esta misma demo, y el de comprar, que además apunta a
    // /comprar, una vista que quedó desactualizada. Las páginas de marketing
    // —/gestion-pie/— siguen siendo enlaces normales.
    abreLogin(plan) {
      const href = plan.href || "";
      return href.startsWith("/utopie/") || href.startsWith("/comprar");
    },
  },
  computed: {
    themeVars() {
      return { "--accent": this.color, "--accent-ink": this.colorInk };
    },
    pasos() {
      return this.content.teaser_steps || [];
    },
    incluye() {
      return this.content.incluye_items || [];
    },
    porque() {
      return this.content.porque || {};
    },
    principios() {
      return this.content.principios || [];
    },
    entrega() {
      return this.content.entrega || [];
    },
    preguntas() {
      return this.content.preguntas || [];
    },
    mineduc() {
      return this.content.mineduc || {};
    },
    cifras() {
      return this.content.cifras || [];
    },
    // El titular de la prueba social es la primera cifra, que es la que la
    // página pone en el <h2> ("160 colegios en Chile").
    cifraTitular() {
      const c = this.cifras[0];
      return c ? `${c.antes || ""}${c.dato} ${c.que}` : "Ya la usan";
    },
    usan() {
      return this.content.usan || [];
    },
    planes() {
      return this.content.planes || [];
    },
    faq() {
      return this.content.faq_items || [];
    },
  },
};
</script>

<style scoped>
/* La paleta de site.css. Va acá y no en :root para no pisarle nada al SPA. */
.svcx {
  --dark: #393e46;
  --light: #dfe2e2;
  --ultralight: #f3f3f3;
  /* `--primary` no lo usa el tema (el acento llega por prop), pero sí el
     contenido: los principios del DUA piden `var(--primary)` para el primero.
     Toda la paleta de site.css tiene que estar, o ese color no resuelve. */
  --primary: #f8b500;
  --primary-dark: #c28e02;
  --secondary: #00adb5;
  --secondary-dark: #227580;
  --redpie: #e74a57;
  --tertiary: #2789ba;
  --purple: #c6588d;
  --ink: var(--dark);
  --ink-soft: #5c636d;
  --ground: #ffffff;
  --radius-lg: 18px;
  --radius-pill: 999px;

  width: 100%;
  text-align: left;
  color: var(--ink);
  line-height: 1.55;
}

.svcx-sec + .svcx-sec {
  margin-top: 2.75rem;
  padding-top: 2.25rem;
  border-top: 1px solid var(--light);
}

.svcx-head {
  max-width: 44rem;
  margin-bottom: 1.5rem;
}

.svcx-head h2 {
  margin: 0;
  font-size: clamp(1.35rem, 2.5vw, 2rem);
  font-weight: 700;
  line-height: 1.25;
}

.svcx-eyebrow {
  display: block;
  margin-bottom: 0.5rem;
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.09em;
  text-transform: uppercase;
  color: var(--accent-ink);
}

/* La franja del titular. El ámbar lleva texto oscuro a propósito: blanco
   sobre #f8ac04 da 1,93:1. */
.svcx-slam {
  display: inline-block;
  margin-top: 0.15em;
  padding: 0.3em 0.75em;
  border-radius: 0.55em;
  background: var(--accent);
  color: var(--ink);
  font-size: 0.95em;
  font-weight: 700;
  text-transform: uppercase;
  line-height: 1.05;
  box-shadow: 0 10px 26px rgba(57, 62, 70, 0.12);
}

.svcx-slam--teal {
  background: var(--secondary-dark);
  color: #fff;
}

.svcx-slam--oscuro {
  background: var(--dark);
  color: #fff;
}

.svcx-slam--rojo {
  background: var(--redpie);
  color: #fff;
}

.svcx-slam--rosa {
  background: var(--purple);
  color: #fff;
}

.svcx-slam--azul {
  background: var(--tertiary);
  color: #fff;
}

/* --- cómo funciona ------------------------------------------------------ */

.svcx-pasos,
.svcx-incluye,
.svcx-preguntas,
.svcx-cifras,
.svcx-usan,
.svcx-planes {
  list-style: none;
  margin: 0;
  padding: 0;
}

.svcx-pasos {
  display: grid;
  gap: 0.7rem;
}

.svcx-pasos li {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1.1rem 1.35rem;
  background: var(--ground);
  border-radius: var(--radius-lg);
  box-shadow: 0 12px 26px rgba(57, 62, 70, 0.09);
}

.svcx-n {
  flex: none;
  width: 2.4rem;
  height: 2.4rem;
  display: grid;
  place-items: center;
  border-radius: 50%;
  background: var(--accent);
  color: var(--ink);
  font-size: 1.15rem;
  font-weight: 700;
}

.svcx-pasos li > div {
  flex: 1;
  min-width: 0;
}

.svcx-pasos b,
.svcx-incluye b {
  display: block;
  font-size: 1.05rem;
}

.svcx-pasos p,
.svcx-incluye p {
  margin: 0.2rem 0 0;
  font-size: 0.95rem;
  color: var(--ink-soft);
}

.svcx-incluye {
  display: grid;
  gap: 0.7rem;
}

.svcx-incluye li {
  padding: 1.1rem 1.35rem;
  background: var(--ground);
  border-left: 8px solid var(--c, var(--accent));
  border-radius: var(--radius-lg);
  box-shadow: 0 12px 26px rgba(57, 62, 70, 0.09);
}

/* --- el porqué ----------------------------------------------------------
   La cadena va sin las fotos que sí lleva la página: sus archivos viven en el
   `static` de Django y con ManifestStaticFilesStorage el nombre lleva hash,
   así que el SPA no puede apuntarlos. El texto solo se sostiene igual. */
.svcx-lede {
  margin: 0.9rem 0 0;
  font-size: 1.05rem;
  color: var(--ink-soft);
}

.svcx-h3 {
  margin: 0 0 0.3rem;
  font-size: 1.15rem;
  font-weight: 700;
}

.svcx-p {
  margin: 0 0 1rem;
  font-size: 0.98rem;
  color: var(--ink-soft);
}

.svcx-cadena {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.7rem;
  list-style: none;
  margin: 0;
  padding: 0;
}

/* Con la misma foto que la página: el que se distrae en el inicio, el que ya
   no entiende el desarrollo, el que se rindió en el cierre. */
.svcx-cadena li {
  background: var(--ground);
  border-top: 8px solid var(--c);
  border-radius: var(--radius-lg);
  overflow: hidden;
  box-shadow: 0 12px 26px rgba(57, 62, 70, 0.09);
}

.svcx-cadena img {
  display: block;
  width: 100%;
  aspect-ratio: 4 / 3;
  object-fit: cover;
}

.svcx-cadena b {
  display: block;
  padding: 0.7rem 0.9rem 0;
  font-size: 1.05rem;
  color: var(--c);
}

.svcx-cadena span {
  display: block;
  padding: 0 0.9rem 0.9rem;
  font-size: 0.9rem;
  color: var(--ink-soft);
}

.svcx-nota {
  margin: 1rem 0 0;
  font-size: 0.98rem;
  color: var(--ink-soft);
}

.svcx-respuesta {
  margin-top: 1.5rem;
  padding: 1.5rem 1.6rem;
  background: var(--ground);
  border-radius: 22px;
  box-shadow: 0 24px 46px rgba(57, 62, 70, 0.15);
}

.svcx-respuesta h3 {
  margin: 0 0 0.5rem;
  font-size: clamp(1.15rem, 1.9vw, 1.45rem);
  font-weight: 700;
}

.svcx-respuesta p {
  margin: 0;
  font-size: 0.98rem;
  line-height: 1.55;
  color: var(--ink-soft);
}

@media (max-width: 575px) {
  .svcx-cadena {
    grid-template-columns: 1fr;
  }
}

/* --- los principios del DUA ---------------------------------------------
   Paneles rellenos del color de cada principio, no fichas blancas: es como
   los pinta .principios en planificaciones.css. `--pink` es el color del
   texto —el ámbar del primero lo necesita oscuro— y por eso no se llama
   `--ink`, que acá ya es el del tema. */
.svcx-principios {
  display: grid;
  gap: 1rem;
}

.svcx-principios li {
  display: grid;
  grid-template-columns: 1fr 1.2fr;
  gap: 0.4rem 2rem;
  align-items: center;
  padding: 1.3rem 1.6rem;
  background: var(--c);
  color: var(--pink, #fff);
  border-radius: 22px;
  box-shadow: 0 12px 26px rgba(57, 62, 70, 0.09);
}

.svcx-principios b {
  font-size: clamp(1.3rem, 2.4vw, 1.8rem);
  line-height: 1.05;
}

.svcx-principios span {
  font-size: 1.1rem;
  font-weight: 500;
  opacity: 0.95;
}

.svcx-principios em {
  grid-column: 2;
  font-style: normal;
  font-size: 0.95rem;
  opacity: 0.9;
}

@media (max-width: 767px) {
  .svcx-principios li {
    grid-template-columns: 1fr;
  }

  .svcx-principios em {
    grid-column: 1;
  }
}

/* --- las preguntas ------------------------------------------------------ */

.svcx-preguntas {
  display: grid;
  gap: 0.7rem;
}

.svcx-preguntas li {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1rem 1.25rem;
  background: var(--ground);
  border-left: 12px solid var(--c);
  border-radius: var(--radius-lg);
  box-shadow: 0 16px 34px rgba(57, 62, 70, 0.14);
}

.svcx-preguntas li::before {
  content: "?";
  flex: none;
  width: 2.4rem;
  height: 2.4rem;
  margin-top: 0.15rem;
  display: grid;
  place-items: center;
  border-radius: 50%;
  background: var(--c);
  color: #fff;
  font-weight: 700;
  font-size: 1.3rem;
}

.svcx-preguntas li > span {
  flex: 1;
  min-width: 0;
}

.svcx-preguntas b {
  display: block;
  font-size: clamp(1.02rem, 1.4vw, 1.2rem);
  font-weight: 700;
}

.svcx-r {
  display: block;
  margin-top: 0.35rem;
  font-size: 0.95rem;
  color: var(--ink-soft);
}

.svcx-r strong {
  color: var(--c);
}

/* Misma excepción de contraste que en site.css: la fila ámbar baja al ink.
   Acá alcanza también al globo del "?", que en la página se queda en blanco
   sobre ámbar (1,93:1). Es la única divergencia deliberada con site.css. */
.svcx-preguntas li[style*="var(--accent)"] .svcx-r strong {
  color: var(--accent-ink);
}

.svcx-preguntas li[style*="var(--accent)"]::before {
  color: var(--ink);
}

/* --- la respuesta del Mineduc ------------------------------------------- */

.svcx-mineduc {
  display: grid;
  grid-template-columns: minmax(0, 1.25fr) minmax(0, 1fr);
  gap: clamp(1.25rem, 3vw, 2rem);
  align-items: center;
}

.svcx-marco {
  position: relative;
  padding: 2.6rem 0.75rem 0.75rem;
  background: var(--dark);
  border-radius: 22px;
  box-shadow: 0 24px 46px rgba(57, 62, 70, 0.15);
}

.svcx-marco iframe {
  display: block;
  width: 100%;
  aspect-ratio: 16 / 9;
  border: 0;
  border-radius: 14px;
  background: var(--ultralight);
}

.svcx-rotulo {
  position: absolute;
  left: 1rem;
  top: 0.8rem;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.85rem;
  font-weight: 700;
  color: #fff;
}

.svcx-rotulo::before {
  content: "";
  width: 0.7rem;
  height: 0.7rem;
  border-radius: 50%;
  background: var(--redpie);
  animation: svcx-parpadea 1s infinite;
}

@keyframes svcx-parpadea {
  50% {
    opacity: 0.15;
  }
}

.svcx-cita {
  padding: 1.5rem 1.6rem;
  background: var(--ground);
  border-radius: 22px;
  box-shadow: 0 12px 26px rgba(57, 62, 70, 0.09);
}

.svcx-cita blockquote {
  margin: 0;
  font-size: clamp(1.05rem, 1.7vw, 1.35rem);
  font-weight: 500;
  line-height: 1.4;
}

.svcx-cita >>> mark {
  background: var(--accent);
  color: var(--ink);
  padding: 0 0.2em;
  border-radius: 4px;
}

.svcx-cita cite {
  display: block;
  margin-top: 0.9rem;
  font-style: normal;
  font-size: 0.92rem;
  color: var(--ink-soft);
}

@media (max-width: 767px) {
  .svcx-mineduc {
    grid-template-columns: 1fr;
  }
}

/* --- prueba social ------------------------------------------------------ */

.svcx-cifras {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.8rem;
}

.svcx-cifras li {
  padding: 1.25rem 1rem;
  background: var(--ground);
  border-radius: var(--radius-lg);
  box-shadow: 0 14px 30px rgba(57, 62, 70, 0.1);
  text-align: center;
}

.svcx-cifras b {
  display: block;
  font-size: clamp(1.9rem, 4vw, 2.9rem);
  font-weight: 700;
  line-height: 1;
  letter-spacing: -0.03em;
  color: var(--secondary-dark);
}

.svcx-cifras b small {
  font-size: 0.55em;
  color: var(--accent-ink);
  vertical-align: 0.3em;
}

.svcx-cifras span {
  display: block;
  margin-top: 0.5rem;
  font-size: 0.92rem;
  font-weight: 500;
  line-height: 1.35;
  color: var(--ink-soft);
}

.svcx-usan {
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
  margin-top: 1.25rem;
}

.svcx-usan li {
  padding: 0.45em 1em;
  border-radius: var(--radius-pill);
  background: var(--ground);
  font-size: 0.9rem;
  font-weight: 500;
  color: var(--ink-soft);
  box-shadow: 0 4px 12px rgba(57, 62, 70, 0.08);
}

/* --- planes ------------------------------------------------------------- */

.svcx-planes {
  display: grid;
  gap: 0.9rem;
}

.svcx-planes li {
  display: grid;
  grid-template-columns: 8rem 1fr auto;
  align-items: center;
  gap: 1.2rem;
  padding: 1.1rem 1.4rem;
  background: var(--ground);
  border-radius: var(--radius-lg);
  box-shadow: 0 10px 24px rgba(57, 62, 70, 0.08);
}

/* El `li` de arriba pinta su propia sombra y es más específico que una clase
   suelta, así que el anillo del acento tiene que nombrar los dos. */
.svcx-planes li.svcx-plan--destacado {
  box-shadow: inset 0 0 0 3px var(--accent), 0 14px 30px rgba(57, 62, 70, 0.12);
}

.svcx-precio {
  font-size: clamp(1.4rem, 2.4vw, 2rem);
  font-weight: 700;
  line-height: 1;
  letter-spacing: -0.03em;
}

.svcx-precio small {
  display: block;
  margin-top: 0.25rem;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--accent-ink);
}

.svcx-planes b {
  display: block;
  font-size: 1.05rem;
}

.svcx-planes p {
  margin: 0.2rem 0 0;
  font-size: 0.92rem;
  line-height: 1.5;
  color: var(--ink-soft);
}

.svcx-btn {
  display: inline-block;
  padding: 0.6em 1.3em;
  border: 2px solid var(--accent-ink);
  border-radius: var(--radius-pill);
  background: var(--accent-ink);
  color: #fff;
  font: inherit;
  font-size: 0.92rem;
  font-weight: 700;
  text-align: center;
  text-decoration: none;
  white-space: nowrap;
  cursor: pointer;
  transition: transform 0.15s ease;
}

.svcx-btn:hover,
.svcx-btn:focus-visible {
  text-decoration: none;
  transform: translateY(-2px);
}

/* El SPA pinta TODO enlace de ámbar con `!important` (a{} en
   assets/scss/main.scss), así que el plan gratis —que es un <button>— salía
   con su texto blanco y los otros dos con letras ámbar sobre fondo ámbar. La
   única forma de ganarle a un `!important` global es otro más específico. */
a.svcx-btn,
a.svcx-btn:hover,
a.svcx-btn:focus-visible {
  color: #fff !important;
}

/* --- preguntas frecuentes ----------------------------------------------- */

.svcx-faq {
  border: 1px solid var(--light);
  border-radius: var(--radius-lg);
  overflow: hidden;
  background: var(--ground);
}

.svcx-faq details + details {
  border-top: 1px solid var(--light);
}

.svcx-faq summary {
  position: relative;
  padding: 1.1rem 3.5rem 1.1rem 1.4rem;
  font-weight: 700;
  cursor: pointer;
  list-style: none;
  transition: background-color 0.25s ease;
}

.svcx-faq summary:hover {
  background: var(--ultralight);
}

.svcx-faq summary::-webkit-details-marker {
  display: none;
}

.svcx-faq summary::after {
  content: "+";
  position: absolute;
  right: 1.1rem;
  top: 50%;
  width: 1.7rem;
  height: 1.7rem;
  display: grid;
  place-items: center;
  transform: translateY(-50%);
  border-radius: 50%;
  background: var(--ultralight);
  font-size: 1.15rem;
  font-weight: 700;
  line-height: 1;
  color: var(--accent-ink);
}

.svcx-faq details[open] summary::after {
  content: "–";
  background: var(--accent);
  color: var(--ink);
}

.svcx-faq details > div {
  padding: 0 1.4rem 1.25rem;
  font-size: 0.95rem;
  color: var(--ink-soft);
}

/* --- levantar al pasar por encima ---------------------------------------
   La ficha responde al puntero: es lo que separa una lista de tarjetas de una
   pila de recuadros. Solo donde hay algo que mirar de cerca —el plan que se
   contrata, el paso que se lee— y nunca en las pastillas de "ya la usan", que
   son un dato al paso. */
.svcx-pasos li,
.svcx-incluye li,
.svcx-principios li,
.svcx-preguntas li,
.svcx-cifras li,
.svcx-planes li {
  transition: transform 0.22s ease, box-shadow 0.22s ease;
}

.svcx-pasos li:hover,
.svcx-incluye li:hover,
.svcx-principios li:hover,
.svcx-cifras li:hover {
  transform: translateY(-4px);
  box-shadow: 0 20px 38px rgba(57, 62, 70, 0.16);
}

.svcx-preguntas li:hover {
  transform: translateY(-4px);
  box-shadow: 0 24px 46px rgba(57, 62, 70, 0.2);
}

/* El destacado conserva su anillo del acento: sin repetirlo acá, el `inset`
   desaparece en cuanto el puntero entra. */
.svcx-planes li:hover {
  transform: translateY(-4px);
  box-shadow: 0 20px 38px rgba(57, 62, 70, 0.14);
}

.svcx-planes li.svcx-plan--destacado:hover {
  box-shadow: inset 0 0 0 3px var(--accent), 0 20px 38px rgba(57, 62, 70, 0.18);
}

/* --- entrada ------------------------------------------------------------
   Una sola animación de entrada para todas las fichas: la página real las
   escalona con `data-anim` y un IntersectionObserver, y acá el bloque entero
   ya llega a la vista de una vez. `--d` mantiene el escalonado. */
.svcx-pasos li,
.svcx-incluye li,
.svcx-principios li,
.svcx-cadena li,
.svcx-preguntas li,
.svcx-cifras li,
.svcx-planes li {
  animation: svcx-in 0.45s backwards;
  animation-delay: var(--d, 0s);
}

/* `backwards`, no `both`: con `both` el fotograma final se queda pegado y su
   `transform` gana al del hover, que entonces no mueve nada. */
@keyframes svcx-in {
  from {
    opacity: 0;
    transform: translateY(14px);
  }
}

@media (prefers-reduced-motion: reduce) {
  .svcx-pasos li,
  .svcx-incluye li,
  .svcx-principios li,
  .svcx-preguntas li,
  .svcx-cifras li,
  .svcx-planes li,
  .svcx-pasos li:hover,
  .svcx-incluye li:hover,
  .svcx-principios li:hover,
  .svcx-preguntas li:hover,
  .svcx-cifras li:hover,
  .svcx-planes li:hover,
  .svcx-btn:hover,
  .svcx-btn:focus-visible,
  .svcx-rotulo::before {
    animation: none;
    transition: none;
    transform: none;
  }
}

@media (max-width: 575px) {
  .svcx-cifras {
    grid-template-columns: 1fr;
  }

  .svcx-planes li {
    grid-template-columns: 1fr;
    gap: 0.55rem;
  }

  .svcx-btn {
    justify-self: start;
  }
}
</style>
